import React from 'react';
import Card from './Card';
import { Box, Typography } from '@mui/material';
import addPriceRanges from '../../utils/addPriceRanges';
import { formatAsDollars } from '../../utils/formatters';
import LinkButton from '../../components/LinkButton';
import CostSection from './CostSection';
import useIncentives from './hooks/useIncentives';

export default function PurchaseCosts({ myProject, incentives, onClickIncentivePrompt }) {
  const costs = myProject.flatMap((room) => room.upfront_costs);

  // Note: Below, everything is called a cost, although some are negative
  // (ie, they are savings).

  const purchaseCosts = costs.filter((cost) => cost.section === 'purchase');
  const installCosts = costs.filter((cost) => cost.section === 'install');
  const emissionCosts = costs.filter((cost) => cost.section === 'emissions');

  const { incentivesAreReady, incentivesAsCosts } = useIncentives({ myProject, incentives });

  const priceRanges = [...purchaseCosts, ...incentivesAsCosts, ...installCosts].map(
    (cost) => cost.price_range,
  );

  const forcePositivePriceRange = (priceRange) => priceRange.map((value) => Math.max(value, 0));

  const totalCostRange = forcePositivePriceRange(addPriceRanges([0, 0], ...priceRanges));

  return (
    <Card>
      <Box mb={3}>
        <CostSection label="Product Purchase" costs={purchaseCosts} />
      </Box>

      {!incentivesAreReady && (
        <Box mb={3} display={{ xs: 'block', sm: 'none' }}>
          <CostSection label="Available Incentives" direction="down" costs={[]}>
            <LinkButton sx={{ fontWeight: '700' }} onClick={onClickIncentivePrompt}>
              Click to see available incentives for you
            </LinkButton>
          </CostSection>
        </Box>
      )}

      {incentivesAreReady && (
        <Box mb={3} display={{ xs: 'block', sm: 'none' }}>
          <CostSection
            label={
              <>
                Available Incentives{' '}
                <LinkButton
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: '700',
                    fontSize: '8pt',
                  }}
                  onClick={onClickIncentivePrompt}
                >
                  Edit
                </LinkButton>
              </>
            }
            direction="down"
            costs={incentivesAsCosts}
          >
            {incentivesAsCosts.length === 0 &&
              'Based on your zip code and income, no rebates are currently available. Check back as new programs are added.'}
          </CostSection>
        </Box>
      )}

      {installCosts.length > 0 && (
        <Box mb={3}>
          <CostSection
            label="Installation Costs"
            tooltip="Installation costs and upgrades may not be needed for every home. Costs may vary. With any technology, you must ensure your panel can carry your household load."
            costs={installCosts}
          />
        </Box>
      )}

      {emissionCosts.length > 0 && (
        <Box mb={3}>
          <CostSection label="Emissions Savings" costs={emissionCosts} />
        </Box>
      )}

      <Box borderTop="1px dotted" borderColor="grey.400" pt={2}>
        <Typography variant="h3" fontSize="1.25rem !important" fontWeight={600} mb={1}>
          Estimated Cost Range
          {incentivesAsCosts.length > 0 && ' with Incentives'}
        </Typography>

        <Typography variant="h3" fontWeight={600} mb={1}>
          {formatAsDollars(totalCostRange[0])} to {formatAsDollars(totalCostRange[1])}
        </Typography>
      </Box>
    </Card>
  );
}
